import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../api/axios';
import FilterSelect from '../../components/FilterSelect';
import { AdminPageHeader } from './AdminUi';
import './admin.css';

const GENDER_OPTIONS = [
  { value: 'M', label: 'Male' },
  { value: 'F', label: 'Female' },
  { value: 'O', label: 'Other' },
];

function Field({ label, value }) {
  return (
    <div className="col-md-6">
      <div className="small text-muted mb-1">{label}</div>
      <div className="fw-semibold" style={{ wordBreak: 'break-word' }}>{value ?? '—'}</div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="col-6 col-md-3">
      <div className="admin-card p-3 text-center h-100">
        <div className="fw-bold fs-4 mb-0">{value ?? 0}</div>
        <div className="small text-muted">{label}</div>
      </div>
    </div>
  );
}

const emptyForm = {
  name: '',
  email: '',
  phone_number: '',
  gender: 'M',
  age: '',
  address: '',
  height: '',
  weight: '',
  dateofbirth: '',
  disease_description: '',
};

export default function AdminUserDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(searchParams.get('edit') === '1');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`admin/users/${userId}/`);
      setUser(r.data);
      setForm({
        name: r.data.name || '',
        email: r.data.email || '',
        phone_number: r.data.phone_number || '',
        gender: r.data.gender || 'M',
        age: r.data.age ?? '',
        address: r.data.address || '',
        height: r.data.height ?? '',
        weight: r.data.weight ?? '',
        dateofbirth: r.data.dateofbirth || '',
        disease_description: r.data.disease_description || '',
      });
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to load user');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.patch(`admin/users/${userId}/`, {
        ...form,
        age: form.age === '' ? form.age : Number(form.age),
        height: form.height === '' ? form.height : Number(form.height),
        weight: form.weight === '' ? form.weight : Number(form.weight),
      });
      setUser(r.data);
      setEditing(false);
      setSearchParams({}, { replace: true });
      toast.success(r.data.msg || 'Patient updated');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    if (!user) return;
    setForm({
      name: user.name || '',
      email: user.email || '',
      phone_number: user.phone_number || '',
      gender: user.gender || 'M',
      age: user.age ?? '',
      address: user.address || '',
      height: user.height ?? '',
      weight: user.weight ?? '',
      dateofbirth: user.dateofbirth || '',
      disease_description: user.disease_description || '',
    });
    setEditing(false);
    setSearchParams({}, { replace: true });
  };

  const handleDelete = async () => {
    if (!user) return;
    const label = user.name || user.email || `#${user.id}`;
    if (
      !window.confirm(
        `Delete patient "${label}"?\n\nThis will also remove their appointments, payments, prescriptions, and related data. This cannot be undone.`
      )
    ) {
      return;
    }
    setDeleting(true);
    try {
      await api.delete(`admin/users/${userId}/`);
      toast.success('Patient deleted successfully');
      navigate('/admin/users', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete patient');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return <div className="text-center py-5"><div className="spinner-border text-primary" /></div>;
  }
  if (!user) {
    return (
      <div>
        <Link to="/admin/users" className="btn btn-outline-secondary rounded-pill mb-3">← Back</Link>
        <div className="alert alert-warning">User not found</div>
      </div>
    );
  }

  const initial = (user.name || 'U').slice(0, 1).toUpperCase();

  return (
    <div>
      <AdminPageHeader title={user.name || 'Patient profile'} subtitle={user.email}>
        <Link to="/admin/users" className="btn btn-outline-secondary rounded-pill px-3">← Back to patients</Link>
        {!editing ? (
          <>
            <button type="button" className="btn btn-primary rounded-pill px-3" onClick={() => setEditing(true)}>
              <i className="bi bi-pencil-square me-1" />
              Edit
            </button>
            <button
              type="button"
              className="btn btn-outline-danger rounded-pill px-3"
              onClick={handleDelete}
              disabled={deleting}
            >
              <i className="bi bi-trash me-1" />
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          </>
        ) : (
          <>
            <button type="button" className="btn btn-outline-secondary rounded-pill px-3" onClick={cancelEdit} disabled={saving}>
              Cancel
            </button>
            <button type="submit" form="admin-user-edit-form" className="btn btn-primary rounded-pill px-3" disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </>
        )}
      </AdminPageHeader>

      <div className="row g-3 mb-3">
        <Stat label="Appointments" value={user.appointments_count} />
        <Stat label="Payments" value={user.payments_count} />
        <Stat label="Prescriptions" value={user.prescriptions_count} />
        <Stat label="Family members" value={user.family_members_count ?? (user.family_members || []).length} />
      </div>

      <div className="row g-3">
        <div className="col-lg-4">
          <div className="admin-card p-4 text-center h-100">
            <div className="admin-profile-photo">
              {user.image ? <img src={user.image} alt={user.name || 'Patient'} /> : initial}
            </div>
            <h5 className="fw-bold mb-1">{user.name}</h5>
            <p className="text-muted small mb-2">{user.email}</p>
            <span className="admin-badge default">Patient #{user.id}</span>
          </div>
        </div>

        <div className="col-lg-8">
          {editing ? (
            <form id="admin-user-edit-form" className="admin-card p-4" onSubmit={save}>
              <h6 className="fw-bold mb-3">Edit patient</h6>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label small fw-semibold">Name</label>
                  <input className="form-control" value={form.name} onChange={set('name')} required />
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-semibold">Email</label>
                  <input type="email" className="form-control" value={form.email} onChange={set('email')} required />
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-semibold">Phone</label>
                  <input className="form-control" value={form.phone_number} onChange={set('phone_number')} />
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-semibold">Gender</label>
                  <FilterSelect
                    value={form.gender}
                    onChange={(val) => setForm((prev) => ({ ...prev, gender: val }))}
                    options={GENDER_OPTIONS}
                    variant="bordered"
                    aria-label="Gender"
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label small fw-semibold">Age</label>
                  <input type="number" className="form-control" value={form.age} onChange={set('age')} />
                </div>
                <div className="col-md-4">
                  <label className="form-label small fw-semibold">Height</label>
                  <input type="number" step="any" className="form-control" value={form.height} onChange={set('height')} />
                </div>
                <div className="col-md-4">
                  <label className="form-label small fw-semibold">Weight</label>
                  <input type="number" step="any" className="form-control" value={form.weight} onChange={set('weight')} />
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-semibold">Date of birth</label>
                  <input type="date" className="form-control" value={form.dateofbirth} onChange={set('dateofbirth')} />
                </div>
                <div className="col-12">
                  <label className="form-label small fw-semibold">Address</label>
                  <textarea className="form-control" rows={2} value={form.address} onChange={set('address')} />
                </div>
                <div className="col-12">
                  <label className="form-label small fw-semibold">Health / disease notes</label>
                  <textarea className="form-control" rows={3} value={form.disease_description} onChange={set('disease_description')} />
                </div>
              </div>
            </form>
          ) : (
            <>
              <div className="admin-card p-4 mb-3">
                <h6 className="fw-bold mb-3">Personal details</h6>
                <div className="row g-3">
                  <Field label="Phone" value={user.phone_number} />
                  <Field label="Gender" value={user.gender} />
                  <Field label="Age" value={user.age} />
                  <Field label="Date of birth" value={user.dateofbirth} />
                  <Field label="Height" value={user.height != null ? `${user.height}` : null} />
                  <Field label="Weight" value={user.weight != null ? `${user.weight}` : null} />
                  <Field label="Address" value={user.address} />
                  <Field
                    label="Joined"
                    value={user.created_at ? new Date(user.created_at).toLocaleString() : null}
                  />
                </div>
              </div>

              <div className="admin-card p-4">
                <h6 className="fw-bold mb-3">Health info</h6>
                <p className="mb-3" style={{ whiteSpace: 'pre-wrap' }}>
                  {user.disease_description || '—'}
                </p>
                {user.medical_record ? (
                  <a href={user.medical_record} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-primary rounded-pill px-3">
                    Open medical record
                  </a>
                ) : (
                  <span className="text-muted small">No medical record uploaded</span>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {!editing && (
        <div className="admin-card p-4 mt-3">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h6 className="fw-bold mb-0">Family members</h6>
            <span className="admin-badge default">
              {(user.family_members || []).length} member{(user.family_members || []).length === 1 ? '' : 's'}
            </span>
          </div>
          {(user.family_members || []).length === 0 ? (
            <p className="text-muted small mb-0">No family members added yet.</p>
          ) : (
            <div className="table-responsive">
              <table className="admin-table mb-0">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Relation</th>
                    <th>Gender</th>
                    <th>Age</th>
                    <th>Phone</th>
                    <th>Medical history</th>
                    <th>Record</th>
                  </tr>
                </thead>
                <tbody>
                  {(user.family_members || []).map((m) => (
                    <tr key={m.id}>
                      <td>
                        <div className="fw-semibold">{m.name}</div>
                      </td>
                      <td>{m.relation || '—'}</td>
                      <td>{m.gender || '—'}</td>
                      <td>{m.age ?? '—'}</td>
                      <td>{m.phone_number || '—'}</td>
                      <td>
                        <div className="small text-truncate" style={{ maxWidth: 220 }} title={m.medical_history || ''}>
                          {m.medical_history || '—'}
                        </div>
                      </td>
                      <td>
                        {m.medical_record ? (
                          <a
                            href={m.medical_record}
                            target="_blank"
                            rel="noreferrer"
                            className="btn btn-sm btn-outline-primary rounded-pill px-3"
                          >
                            View
                          </a>
                        ) : (
                          <span className="text-muted small">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
